/**
 * custom-page-voice-clock.js
 * ORIGINAL VOICE PREMIUM CLOCK LP
 *
 * 読み込み元: snippets/custom-pages-monster-strike-voice-premium-clock-2610.liquid（defer）
 *
 * 担当:
 * - タブ切替（天使シリーズ / 降臨）… URL ハッシュ #angel / #bgm で直リンク可
 * - タブ行の上部固定（sticky）… テーマの追従ヘッダーの出入りに合わせて位置をずらす
 * - VOICE LIST / BGM LIST の SP 用「リストを見る／閉じる」
 * - FAQ アコーディオン
 * - サンプル音声プレイヤー（共有 Audio 1本。他を再生したら前のは止める）
 * - 音源の先読み（描画完了後にバックグラウンドでブラウザのキャッシュへ載せる）
 * - デザインギャラリー（Swiper はテーマ側 layout/theme.liquid で読み込み済み）
 * - 「台座もあわせてカートに追加」（/cart/add.js で2点同時追加）
 * - デザイン確認モード中に出している、買えない商品の購入ボタン（押すと案内だけ出す）
 *
 * 方針:
 * - IIFE で包み、グローバルには何も出さない
 * - ルート要素の data-initialized で二重初期化を防ぐ（テーマエディタの再描画対策）
 * - shopify:section:load / unload に対応
 * - 先読みは「速くなれば儲けもの」の位置づけ。キャッシュから押し出されていても
 *   従来どおり読み込んで再生できるよう、フォールバックを必ず残す
 */
(() => {
  'use strict';

  const SELECTOR = Object.freeze({
    root: '[data-voice-clock-page]',
    tab: '[data-voice-clock-tab]',
    panel: '[data-voice-clock-panel]',
    series: '[data-voice-clock-series]',
    tabBar: '[data-voice-clock-tabbar]',
    tabsTitle: '.c-page-voice-clock__tabs-title',
    stickyHeader: 'store-header[sticky]',
    listBlock: '[data-voice-clock-list]',
    listToggle: '[data-voice-clock-list-toggle]',
    listToggleLabel: '[data-voice-clock-list-toggle-label]',
    accordionTrigger: '[data-voice-clock-accordion-trigger]',
    play: '[data-voice-clock-play]',
    playLabel: '[data-voice-clock-play-label]',
    voice: '[data-voice-clock-voice]',
    time: '[data-voice-clock-time]',
    gallery: '[data-voice-clock-gallery]',
    gallerySwiper: '[data-voice-clock-gallery-swiper]',
    galleryPagination: '[data-voice-clock-gallery-pagination]',
    galleryPrev: '[data-voice-clock-gallery-prev]',
    galleryNext: '[data-voice-clock-gallery-next]',
    thumb: '[data-voice-clock-thumb]',
    addSet: '[data-voice-clock-add-set]',
    unavailable: '[data-voice-clock-unavailable]',
  });

  const CLASS = Object.freeze({
    active: 'is-active',
    playing: 'is-playing',
    loading: 'is-loading',
    open: 'is-open',
    stuck: 'is-stuck',
    headerHidden: 'header-section--hidden',
  });

  /** 再生ボタンの状態 */
  const PLAY_STATE = Object.freeze({
    idle: 'idle',
    loading: 'loading',
    playing: 'playing',
  });

  const EVENT = Object.freeze({
    panelChange: 'voice-clock:panel-change',
    stopAudio: 'voice-clock:stop-audio',
    teardown: 'voice-clock:teardown',
  });

  const TAB_IDS = Object.freeze(['angel', 'bgm']);
  const DEFAULT_TAB = 'angel';
  /** タブへスクロールする際に上部に残す余白（追尾ヘッダー分） */
  const SCROLL_OFFSET_PX = 80;
  /** 固定中のタブ行と画面上端（またはヘッダー）の間の余白。CSS の .c-page-voice-clock__tabs の top と揃える */
  const TAB_BAR_GAP_PX = 8;
  const LABEL_PLAY = 'PLAY';
  const LABEL_STOP = 'STOP';
  // 音源は wav（降臨BGMは1本7〜8MB）のため、押下から再生開始までの間を明示する
  const LABEL_LOADING = 'LOADING';
  const CART_URL = '/cart';
  const CART_ADD_URL = '/cart/add.js';
  const ADD_SET_ERROR_MESSAGE = 'カートに追加できませんでした。時間をおいて再度お試しください。';
  // デザイン確認モード（snippet の design_review）で、買えない商品のボタンを Figma どおりの見た目で出しているとき
  const UNAVAILABLE_MESSAGE = '現在カートに追加できません。';
  const ADD_SET_UNAVAILABLE_MESSAGE = '現在、台座とのセットではカートに追加できません。';

  /**
   * パネルごとに先読みする本数（Shopify Files の実サイズ実測値にもとづく）
   *   angel … 12本で合計 約11.7MB（1本 0.67〜1.25MB）→ 全件取ってよい
   *   bgm   … 6本で合計 約47.3MB（1本 7.52〜8.69MB）→ 全件は落とさせない
   * initial: 描画完了後の自動先読み / engaged: 一度でも再生した後
   */
  const PREFETCH_LIMIT = Object.freeze({
    angel: Object.freeze({ initial: Infinity, engaged: Infinity }),
    bgm: Object.freeze({ initial: 2, engaged: 4 }),
  });

  /** この回線種別のときは先読みしない */
  const SLOW_CONNECTIONS = Object.freeze(['slow-2g', '2g', '3g']);

  /** requestIdleCallback が来ないまま経過したら先読みを始める */
  const PREFETCH_IDLE_TIMEOUT_MS = 2000;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * 秒数を mm:ss に整形する
   * @param {number} seconds
   * @returns {string}
   */
  const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
    const minutes = String(Math.floor(total / 60)).padStart(2, '0');
    const rest = String(total % 60).padStart(2, '0');
    return `${minutes}:${rest}`;
  };

  /**
   * 要素の上端を画面上部（オフセット付き）へスクロールする
   * @param {Element} el
   */
  const scrollToTop = (el) => {
    const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  /**
   * テーマの追従ヘッダー（sections/header.liquid）が今画面に出ている高さ。
   * sticky-up 設定なので下スクロールで隠れ（header-section--hidden）、上スクロールで出てくる。
   * 追従しない設定・ヘッダーが無いときは 0。
   * @param {{ includeHidden?: boolean }} [options] true なら今は隠れていても高さを返す
   * @returns {number}
   */
  const getHeaderOffset = ({ includeHidden = false } = {}) => {
    const header = document.querySelector(SELECTOR.stickyHeader);
    const section = header && header.parentElement;
    if (!section) return 0;
    if (!includeHidden && section.classList.contains(CLASS.headerHidden)) return 0;
    return section.offsetHeight;
  };

  /* ------------------------------------------------------------
     タブ行の上部固定
     ------------------------------------------------------------ */
  /**
   * 固定自体は CSS の position: sticky。ここでは
   * - ヘッダーが出ている間だけその高さを --vc-sticky-top に入れて、タブ行をヘッダーの下へずらす
   * - 固定中かどうかを判定して .is-stuck を付ける（下敷きの表示と重なり順の切り替え用）
   * だけを行う。
   * @param {HTMLElement} root
   */
  const initStickyTabs = (root) => {
    const tabBar = root.querySelector(SELECTOR.tabBar);
    const tabsTitle = root.querySelector(SELECTOR.tabsTitle);
    if (!tabBar || !tabsTitle) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      root.style.setProperty('--vc-sticky-top', `${getHeaderOffset()}px`);
      // 本来の位置（見出しの下）より下にずれていれば固定中。top のアニメーション中でも判定がぶれない
      const naturalTop =
        tabsTitle.getBoundingClientRect().bottom + parseFloat(getComputedStyle(tabsTitle).marginBottom || '0');
      const stuck = tabBar.getBoundingClientRect().top - naturalTop > 0.5;
      tabBar.classList.toggle(CLASS.stuck, stuck);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    // ヘッダーの出入りはクラスの付け外しで起きるので、それも拾う
    const header = document.querySelector(SELECTOR.stickyHeader);
    const headerSection = header && header.parentElement;
    let observer = null;
    if (headerSection && 'MutationObserver' in window) {
      observer = new MutationObserver(schedule);
      observer.observe(headerSection, { attributes: true, attributeFilter: ['class'] });
    }

    root.addEventListener(
      EVENT.teardown,
      () => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        if (observer) observer.disconnect();
        if (frame) cancelAnimationFrame(frame);
      },
      { once: true },
    );

    update();
  };

  /* ------------------------------------------------------------
     タブ切替
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement} root
   */
  const initTabs = (root) => {
    const tabs = [...root.querySelectorAll(SELECTOR.tab)];
    const panels = [...root.querySelectorAll(SELECTOR.panel)];
    if (!tabs.length || !panels.length) return;

    const series = root.querySelector(SELECTOR.series) || tabs[0];
    const tabBar = root.querySelector(SELECTOR.tabBar);
    const tabsTitle = root.querySelector(SELECTOR.tabsTitle);

    /**
     * タブ行が固定される直前（＝本来の位置のまま画面上部に来た瞬間）のスクロール位置。
     * 固定中にタブを切り替えたら、新しいパネルをこの位置＝パネルの頭から見せる。
     * @returns {number | null}
     */
    const getPanelsStartScrollY = () => {
      if (!tabBar || !tabsTitle) return null;
      const titleBottom = tabsTitle.getBoundingClientRect().bottom + window.scrollY;
      const naturalTop = titleBottom + parseFloat(getComputedStyle(tabsTitle).marginBottom || '0');
      return naturalTop - TAB_BAR_GAP_PX;
    };

    /** 固定中のときだけ、新しいパネルの頭まで戻す。固定前ならパネルの頭はもう見えているので動かさない */
    const scrollToPanelsStart = () => {
      const start = getPanelsStartScrollY();
      if (start === null || window.scrollY <= start + 1) return;
      // 上へ戻るのでテーマのヘッダーが出てくる（今は隠れていても）。その高さぶん手前で止める
      const top = start - getHeaderOffset({ includeHidden: true });
      window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    /**
     * @param {string} id
     * @param {{ scroll?: boolean | 'panels', updateHash?: boolean }} [options]
     *   scroll: true = 節の頭へ（ハッシュ直リンク用）／ 'panels' = 固定中ならパネルの頭へ
     */
    const activate = (id, { scroll = false, updateHash = false } = {}) => {
      if (!TAB_IDS.includes(id)) return;

      tabs.forEach((tab) => {
        const on = tab.dataset.voiceClockTab === id;
        tab.classList.toggle(CLASS.active, on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.voiceClockPanel !== id;
      });
      // 背景帯の地紋（天使＝六角 / 降臨＝無地）を CSS 側で切り替えるためのフラグ
      root.dataset.voiceClockActive = id;

      root.dispatchEvent(new CustomEvent(EVENT.stopAudio));
      root.dispatchEvent(new CustomEvent(EVENT.panelChange, { detail: { id } }));

      if (updateHash && window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState(null, '', `#${id}`);
      }
      if (scroll === 'panels') scrollToPanelsStart();
      else if (scroll) scrollToTop(series);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        if (tab.classList.contains(CLASS.active)) return;
        activate(tab.dataset.voiceClockTab, { scroll: 'panels', updateHash: true });
      });
      // 矢印キーでタブ間を移動（WAI-ARIA tabs パターン）
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(index + delta + tabs.length) % tabs.length];
        next.focus();
        activate(next.dataset.voiceClockTab, { updateHash: true });
      });
    });

    const readHash = () => window.location.hash.replace('#', '');
    const initial = readHash();
    const hasInitialHash = TAB_IDS.includes(initial);
    activate(hasInitialHash ? initial : DEFAULT_TAB);
    if (hasInitialHash) {
      requestAnimationFrame(() => scrollToTop(series));
    }

    window.addEventListener('hashchange', () => {
      const id = readHash();
      if (TAB_IDS.includes(id)) activate(id, { scroll: true });
    });
  };

  /* ------------------------------------------------------------
     FAQ アコーディオン
     ------------------------------------------------------------ */
  /** .c-vc-qa__answer の transition と揃える */
  const ACCORDION_DURATION_MS = 350;

  /**
   * @param {HTMLElement} root
   */
  const initAccordions = (root) => {
    root.querySelectorAll(SELECTOR.accordionTrigger).forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls') || '');
      if (!panel) return;

      let timer = 0;
      /** @type {((event: TransitionEvent) => void) | null} */
      let onEnd = null;

      const cancel = () => {
        if (timer) {
          window.clearTimeout(timer);
          timer = 0;
        }
        if (onEnd) {
          panel.removeEventListener('transitionend', onEnd);
          onEnd = null;
        }
      };

      /**
       * @param {boolean} open
       * @param {boolean} animate
       */
      const setOpen = (open, animate) => {
        cancel();
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

        const finish = () => {
          cancel();
          panel.hidden = !open;
          panel.style.height = '';
          panel.style.overflow = '';
        };

        if (!animate || prefersReducedMotion()) {
          finish();
          return;
        }

        const from = panel.hidden ? 0 : panel.getBoundingClientRect().height;
        panel.hidden = false;
        panel.style.overflow = 'hidden';
        panel.style.height = 'auto';
        const full = panel.getBoundingClientRect().height;
        const to = open ? full : 0;

        panel.style.height = `${from}px`;
        panel.getBoundingClientRect();
        panel.style.height = `${to}px`;

        onEnd = (event) => {
          if (event.target !== panel || event.propertyName !== 'height') return;
          finish();
        };
        panel.addEventListener('transitionend', onEnd);
        timer = window.setTimeout(finish, ACCORDION_DURATION_MS + 80);
      };

      setOpen(trigger.getAttribute('aria-expanded') !== 'false', false);
      trigger.addEventListener('click', () => {
        setOpen(trigger.getAttribute('aria-expanded') === 'false', true);
      });
    });
  };

  /* ------------------------------------------------------------
     VOICE LIST / BGM LIST（SP のみ表示されるトグル）
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement} root
   */
  const initListToggles = (root) => {
    root.querySelectorAll(SELECTOR.listBlock).forEach((block) => {
      const toggle = block.querySelector(SELECTOR.listToggle);
      if (!toggle) return;
      const label = block.querySelector(SELECTOR.listToggleLabel);

      const setOpen = (open) => {
        block.classList.toggle(CLASS.open, open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) {
          label.textContent = open ? toggle.dataset.labelOpen || '' : toggle.dataset.labelClosed || '';
        }
      };

      setOpen(false);
      toggle.addEventListener('click', () => {
        const willOpen = !block.classList.contains(CLASS.open);
        setOpen(willOpen);
        // 閉じたときにリスト先頭が画面外なら戻す
        if (!willOpen && block.getBoundingClientRect().top < 0) scrollToTop(block);
      });
    });
  };

  /* ------------------------------------------------------------
     音源の先読み（プリフェッチ）
     ------------------------------------------------------------ */
  /**
   * 先読みしてよい回線かどうか。
   * 省データモードや低速回線では、見ているだけのユーザーに数十MB落とさない。
   * Network Information API 非対応（Safari 等）は判定材料がないので許可する。
   * @returns {boolean}
   */
  const canPrefetch = () => {
    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    if (connection.saveData) return false;
    return !SLOW_CONNECTIONS.includes(connection.effectiveType);
  };

  /**
   * 画面の描画が落ち着いてから実行する
   * @param {() => void} callback
   */
  const whenIdle = (callback) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: PREFETCH_IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(callback, PREFETCH_IDLE_TIMEOUT_MS);
    }
  };

  /**
   * 音源をブラウザのキャッシュに載せておくキュー。
   *
   * - 帯域を食い合わないよう、常に1本ずつ直列に取得する
   * - 受け取った中身は読み捨てる。メモリには残さず、残るのはブラウザのキャッシュだけ
   * - mode: 'cors' は Audio 要素側の crossOrigin='anonymous' と揃えるため。
   *   ずれるとキャッシュキーが一致せず、同じファイルを2回落とすことになる
   */
  const createPrefetcher = () => {
    /** @type {Set<string>} 取得済み */
    const done = new Set();
    /** @type {string[]} 取得待ち */
    const queue = [];
    /** @type {string | null} 最優先で取りにいく1本（ホバー／タッチ） */
    let priority = null;
    /** @type {string | null} いま取得中の URL */
    let inflight = null;
    /** @type {AbortController | null} */
    let controller = null;
    let running = false;
    let paused = false;
    /** clear() で繰り上がる世代番号。古い世代の中断ぶんはキューに戻さない */
    let generation = 0;

    /**
     * @param {string} url
     */
    const fetchOne = async (url) => {
      const era = generation;
      inflight = url;
      controller = new AbortController();
      try {
        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'omit',
          cache: 'force-cache',
          signal: controller.signal,
        });
        if (response.ok) {
          const reader = response.body ? response.body.getReader() : null;
          if (reader) {
            // 中身は使わないので読み捨てる。これでキャッシュにだけ残る
            for (;;) {
              const chunk = await reader.read();
              if (chunk.done) break;
            }
          } else {
            await response.arrayBuffer();
          }
          done.add(url);
        }
      } catch (error) {
        // 中断されたぶんはキューの先頭に戻して、あとで取り直す
        // （clear() 済みの古い世代のものは、戻さずそのまま捨てる）
        if (error && error.name === 'AbortError' && era === generation && !done.has(url)) {
          queue.unshift(url);
        }
      } finally {
        inflight = null;
        controller = null;
      }
    };

    const run = async () => {
      if (running) return;
      running = true;
      while (!paused && canPrefetch()) {
        const url = priority || queue.shift();
        priority = null;
        if (!url) break;
        if (done.has(url)) continue;
        await fetchOne(url);
      }
      running = false;
    };

    return {
      /**
       * 先読み待ちに積む（取得済み・取得中のものは無視される）
       * @param {string[]} urls
       */
      enqueue(urls) {
        urls.forEach((url) => {
          if (!url || done.has(url) || url === inflight || queue.includes(url)) return;
          queue.push(url);
        });
        run();
      },
      /**
       * この1本を最優先で取りにいく。取得中のものがあれば中断して帯域を回す
       * @param {string} url
       */
      promote(url) {
        if (!url || done.has(url) || inflight === url || priority === url) return;
        priority = url;
        if (controller) controller.abort();
        run();
      },
      /** 取得を中断する。キューの中身は残す */
      pause() {
        paused = true;
        if (controller) controller.abort();
      },
      /** 中断していた取得を再開する */
      resume() {
        if (!paused) return;
        paused = false;
        run();
      },
      /** キューごと破棄する（取得済みの記録は残す） */
      clear() {
        generation += 1;
        queue.length = 0;
        priority = null;
        if (controller) controller.abort();
      },
    };
  };

  /* ------------------------------------------------------------
     サンプル音声プレイヤー
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement} root
   */
  const initAudioPlayers = (root) => {
    const buttons = [...root.querySelectorAll(SELECTOR.play)];
    if (!buttons.length) return;

    const player = new Audio();
    player.preload = 'none';
    // 先読みの fetch と同じ「匿名 CORS リクエスト」にして、キャッシュキーを揃える
    player.crossOrigin = 'anonymous';

    const prefetcher = createPrefetcher();
    // document / window に張るリスナーは、セクション破棄時にまとめて外す
    const listeners = new AbortController();

    /** @type {HTMLButtonElement | null} 再生中のボタン */
    let current = null;
    /** CORS 付きで再生できなかったとき、1度だけ crossOrigin を外して試す */
    let crossOriginRetried = false;
    /** 次の再生で src を張り直す（crossOrigin を切り替えた直後に使う） */
    let forceReload = false;
    /** window の load 前は先読みしない（画面の描画を優先する） */
    let pageLoaded = document.readyState === 'complete';

    /**
     * @param {HTMLButtonElement} button
     * @returns {string}
     */
    const srcOf = (button) => {
      const src = button.dataset.voiceSrc;
      return src ? new URL(src, window.location.href).href : '';
    };

    const timeElementOf = (button) => {
      const voice = button.closest(SELECTOR.voice);
      return voice ? voice.querySelector(SELECTOR.time) : null;
    };

    /**
     * パネル内の音源 URL を、先読みしてよい本数だけ返す
     * @param {string} panelId
     * @param {'initial' | 'engaged'} phase
     * @returns {string[]}
     */
    const prefetchListOf = (panelId, phase) => {
      const limit = PREFETCH_LIMIT[panelId];
      const panel = root.querySelector(`[data-voice-clock-panel="${panelId}"]`);
      if (!limit || !panel) return [];

      const urls = [];
      panel.querySelectorAll(SELECTOR.play).forEach((button) => {
        const url = srcOf(button);
        if (url && !urls.includes(url)) urls.push(url);
      });
      return Number.isFinite(limit[phase]) ? urls.slice(0, limit[phase]) : urls;
    };

    /**
     * 表示中のパネルぶんを先読みキューに積む
     * @param {'initial' | 'engaged'} phase
     */
    const queueActivePanel = (phase) => {
      if (!pageLoaded) return;
      const panelId = root.dataset.voiceClockActive;
      if (panelId) prefetcher.enqueue(prefetchListOf(panelId, phase));
    };

    /**
     * @param {HTMLButtonElement} button
     * @param {'idle' | 'loading' | 'playing'} state
     */
    const setButtonState = (button, state) => {
      button.classList.toggle(CLASS.playing, state === PLAY_STATE.playing);
      button.classList.toggle(CLASS.loading, state === PLAY_STATE.loading);
      button.setAttribute('aria-pressed', state === PLAY_STATE.idle ? 'false' : 'true');
      button.setAttribute('aria-busy', state === PLAY_STATE.loading ? 'true' : 'false');

      const label = button.querySelector(SELECTOR.playLabel);
      if (label) {
        if (state === PLAY_STATE.playing) {
          label.textContent = LABEL_STOP;
        } else if (state === PLAY_STATE.loading) {
          label.textContent = LABEL_LOADING;
        } else {
          label.textContent = LABEL_PLAY;
        }
      }

      if (state === PLAY_STATE.idle) {
        const time = timeElementOf(button);
        if (time) time.textContent = formatTime(0);
      }
    };

    /**
     * 読み込み中は、バッファ済みの割合を % で出す。
     * 先読みが効いていれば一瞬で消えるが、キャッシュから押し出されていた場合の受け皿になる。
     */
    const updateProgress = () => {
      if (!current || !current.classList.contains(CLASS.loading)) return;
      if (!Number.isFinite(player.duration) || player.duration <= 0) return;
      if (!player.buffered.length) return;

      const buffered = player.buffered.end(player.buffered.length - 1);
      const percent = Math.min(99, Math.max(0, Math.round((buffered / player.duration) * 100)));
      const label = current.querySelector(SELECTOR.playLabel);
      if (label) label.textContent = `${percent}%`;
    };

    /**
     * 再生を止めてボタンを戻す
     * @param {{ resumePrefetch?: boolean }} [options] 続けて再生する場合は先読みを再開しない
     */
    const stop = ({ resumePrefetch = true } = {}) => {
      if (!current) return;
      player.pause();
      try {
        player.currentTime = 0;
      } catch (_) {
        /* src 未設定時は currentTime を触れないことがある */
      }
      setButtonState(current, PLAY_STATE.idle);
      current = null;
      // 再生のために止めていた先読みを再開する
      if (resumePrefetch) prefetcher.resume();
    };

    /**
     * @param {HTMLButtonElement} button
     */
    const play = async (button) => {
      const resolved = srcOf(button);
      if (!resolved) return;

      // このあとすぐ pause() するので、ここでは先読みを再開させない
      stop({ resumePrefetch: false });
      current = button;
      // 先読み済みなら一瞬で消えるが、押し出されていた場合に備えて LOADING から始める
      setButtonState(button, PLAY_STATE.loading);
      // 再生と帯域を奪い合わないよう、バックグラウンドの先読みはいったん止める
      prefetcher.pause();

      if (player.src !== resolved || forceReload) {
        forceReload = false;
        player.src = resolved;
        player.load();
      }

      try {
        await player.play();
        if (current === button) setButtonState(button, PLAY_STATE.playing);
      } catch (_) {
        // 再生に失敗（ファイル未配置・ブラウザ制限など）→ 表示を戻す
        if (current === button) stop();
      }
      // 一度でも再生したユーザーには、残りも積極的に先読みしておく
      queueActivePanel('engaged');
    };

    /**
     * 読み込みエラー。CORS が原因の可能性があるので、1度だけ crossOrigin を外して取り直す
     */
    const handleError = () => {
      const retryButton = current;
      if (crossOriginRetried || !player.crossOrigin || !retryButton) {
        stop();
        return;
      }
      crossOriginRetried = true;
      forceReload = true;
      player.removeAttribute('crossorigin');
      current = null;
      play(retryButton);
    };

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        if (current === button) {
          stop();
        } else {
          play(button);
        }
      });
      // 押す直前（マウスを乗せた／指が触れた）時点で、その1本を最優先で取りにいく
      const promote = () => prefetcher.promote(srcOf(button));
      button.addEventListener('pointerenter', promote);
      button.addEventListener('pointerdown', promote);
    });

    // play() の解決を待たずに再生が始まる場合の保険
    player.addEventListener('playing', () => {
      if (current) setButtonState(current, PLAY_STATE.playing);
    });
    player.addEventListener('waiting', () => {
      if (current) setButtonState(current, PLAY_STATE.loading);
    });
    player.addEventListener('progress', updateProgress);
    player.addEventListener('durationchange', updateProgress);
    // 最後まで途切れず再生できる見込みが立ったら、先読みを再開してよい
    player.addEventListener('canplaythrough', () => prefetcher.resume());
    player.addEventListener('timeupdate', () => {
      if (!current) return;
      const time = timeElementOf(current);
      if (time) time.textContent = formatTime(player.currentTime);
    });
    player.addEventListener('ended', () => stop());
    player.addEventListener('error', handleError);

    root.addEventListener(EVENT.stopAudio, () => stop());

    // タブを切り替えたら、隠れたパネルの先読みは捨てて、表示されたパネルを積み直す
    root.addEventListener(EVENT.panelChange, () => {
      prefetcher.clear();
      queueActivePanel('initial');
    });

    root.addEventListener(EVENT.teardown, () => {
      prefetcher.clear();
      listeners.abort();
    });

    // 別タブ・別アプリに移っている間は帯域を使わない
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) prefetcher.pause();
        else prefetcher.resume();
      },
      { signal: listeners.signal }
    );

    // 画面の描画がすべて終わってから、アイドル時間を使って先読みを始める
    const startPrefetch = () => {
      pageLoaded = true;
      whenIdle(() => queueActivePanel('initial'));
    };
    if (pageLoaded) {
      startPrefetch();
    } else {
      window.addEventListener('load', startPrefetch, { once: true, signal: listeners.signal });
    }
  };

  /* ------------------------------------------------------------
     デザインギャラリー（Swiper）
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement} root
   */
  const initGalleries = (root) => {
    if (typeof window.Swiper === 'undefined') return;

    root.querySelectorAll(SELECTOR.gallery).forEach((gallery) => {
      const el = gallery.querySelector(SELECTOR.gallerySwiper);
      if (!el || el.swiper) return;

      const thumbs = [...gallery.querySelectorAll(SELECTOR.thumb)];
      const setActiveThumb = (index) => {
        thumbs.forEach((thumb, i) => {
          const on = i === index;
          thumb.classList.toggle(CLASS.active, on);
          thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      };

      const swiper = new window.Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 0,
        watchOverflow: true,
        pagination: {
          el: gallery.querySelector(SELECTOR.galleryPagination),
          clickable: true,
        },
        navigation: {
          prevEl: gallery.querySelector(SELECTOR.galleryPrev),
          nextEl: gallery.querySelector(SELECTOR.galleryNext),
        },
        a11y: {
          prevSlideMessage: '前のデザイン画像',
          nextSlideMessage: '次のデザイン画像',
        },
        on: {
          slideChange() {
            setActiveThumb(this.activeIndex);
          },
        },
      });

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => swiper.slideTo(index));
      });

      // 非表示パネル内で初期化されると幅 0 で計算されるので、表示時に再計算
      root.addEventListener(EVENT.panelChange, () => {
        if (!el.isConnected || el.closest('[hidden]')) return;
        requestAnimationFrame(() => swiper.update());
      });
    });
  };

  /* ------------------------------------------------------------
     台座もあわせてカートに追加
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement} root
   */
  const initAddSet = (root) => {
    root.querySelectorAll(SELECTOR.addSet).forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        if (button.getAttribute('aria-disabled') === 'true') {
          window.alert(ADD_SET_UNAVAILABLE_MESSAGE);
          return;
        }
        const ids = (button.dataset.variantIds || '')
          .split(',')
          .map((value) => Number(value.trim()))
          .filter((value) => Number.isInteger(value) && value > 0);
        if (!ids.length) return;

        button.disabled = true;
        try {
          const response = await fetch(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
          });
          if (!response.ok) throw new Error(`cart/add.js responded ${response.status}`);
          window.location.href = CART_URL;
        } catch (_) {
          button.disabled = false;
          window.alert(ADD_SET_ERROR_MESSAGE);
        }
      });
    });
  };

  /**
   * デザイン確認モードで出している、買えない時計の「カートに追加」。押すと案内だけ出す
   * @param {HTMLElement} root
   */
  const initUnavailable = (root) => {
    root.querySelectorAll(SELECTOR.unavailable).forEach((button) => {
      button.addEventListener('click', () => window.alert(UNAVAILABLE_MESSAGE));
    });
  };

  /* ------------------------------------------------------------
     起動
     ------------------------------------------------------------ */
  /**
   * @param {HTMLElement | null} root
   */
  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    initAccordions(root);
    initListToggles(root);
    initAudioPlayers(root);
    initGalleries(root);
    initAddSet(root);
    initUnavailable(root);
    initStickyTabs(root);
    // タブは最後に初期化する（初期表示時に panel-change / stop-audio を投げるため）
    initTabs(root);
  };

  const boot = () => {
    document.querySelectorAll(SELECTOR.root).forEach((root) => init(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!target || typeof target.querySelectorAll !== 'function') return;
    target.querySelectorAll(SELECTOR.root).forEach((root) => init(root));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!target || typeof target.querySelectorAll !== 'function') return;
    target.querySelectorAll(SELECTOR.root).forEach((root) => {
      root.dispatchEvent(new CustomEvent(EVENT.stopAudio));
      // 先読みを打ち切り、document に張ったリスナーも外す
      root.dispatchEvent(new CustomEvent(EVENT.teardown));
    });
  });
})();
